import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasManagerAccess } from '../App';
import { api } from '../lib/api';
import StatusBadge from '../components/StatusBadge';
import { ArrowLeft, DollarSign, Plus, Edit2, Save, X, Calendar, MapPin, CheckCircle, CheckCircle2, Clock, Zap, ChevronRight, AlertCircle, User, Ruler, Palette, Trash2 } from 'lucide-react';

const PIPELINE = [
  { key: 'pending',          label: 'New Sale',           desc: 'Check inventory & color',          icon: '📋' },
  { key: 'color_match',      label: 'Color Match',        desc: 'Waiting on color matching',         icon: '🎨' },
  { key: 'confirmed',        label: 'Confirmed',          desc: 'Contact customer for install date', icon: '✅' },
  { key: 'scheduled',        label: 'Scheduled',          desc: 'Install date locked in',            icon: '📅' },
  { key: 'in_progress',      label: 'Day of Install',     desc: 'Installation in progress',          icon: '🔧' },
  { key: 'awaiting_payment', label: 'Awaiting Payment',   desc: 'Installed — collecting payment',    icon: '💳' },
  { key: 'completed',        label: 'Installed & Collected', desc: 'Payment received',               icon: '🏠' },
];
const paymentLabels = { cash: 'Cash', etransfer: 'E-Transfer', cc: 'Credit Card', financed: 'Financed (In-House)' };
const payColor = { cash: 'bg-green-100 text-green-700', etransfer: 'bg-blue-100 text-blue-700', cc: 'bg-violet-100 text-violet-700', financed: 'bg-orange-100 text-orange-700' };
const fmt = (n) => '$' + (n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2 });
const inputCls = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-nova-500 focus:border-nova-500 outline-none text-sm';
const labelCls = 'block text-xs text-gray-500 mb-1';

export default function SaleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sale, setSale] = useState(null);
  const [colors, setColors] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payment, setPayment] = useState({
    amount: '', payment_method: '', notes: '',
    payment_date: new Date().toISOString().split('T')[0],
  });
  const [scheduleSuggestions, setScheduleSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [matchedColorId, setMatchedColorId] = useState('');
  const [collectPayment, setCollectPayment] = useState({ amount: '', method: '', notes: '' });
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({});
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editingRotessa, setEditingRotessa] = useState(false);
  const [rotessaForm, setRotessaForm] = useState({ start_date: '', rotessa_link: '', notes: '' });
  const [salesmen, setSalesmen] = useState([]);
  const [editingPayment, setEditingPayment] = useState(null); // payment being edited
  const [editPaymentForm, setEditPaymentForm] = useState({ amount: '', payment_method: '', payment_date: '', notes: '' });
  const [savingPaymentEdit, setSavingPaymentEdit] = useState(false);

  const openEditPayment = (p) => {
    setEditingPayment(p);
    setEditPaymentForm({
      amount: p.amount ?? '',
      payment_method: p.payment_method || '',
      payment_date: p.payment_date || new Date().toISOString().split('T')[0],
      notes: p.notes || '',
    });
  };
  const closeEditPayment = () => setEditingPayment(null);
  const saveEditPayment = async () => {
    if (!editingPayment) return;
    setSavingPaymentEdit(true);
    try {
      await api.put(`/payments/${editingPayment.id}`, {
        amount: Number(editPaymentForm.amount),
        payment_method: editPaymentForm.payment_method,
        payment_date: editPaymentForm.payment_date,
        notes: editPaymentForm.notes,
      });
      setEditingPayment(null);
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setSavingPaymentEdit(false);
    }
  };

  // Managers can edit which salesman owns the sale
  useEffect(() => {
    if (hasManagerAccess(user)) {
      api.get('/users/salesmen').then(setSalesmen).catch(() => {});
    }
  }, [user]);

  const load = () => api.get(`/sales/${id}`).then(setSale);

  // Auto-recalculate monthly when deal total, deposit, after-install, or term changes in edit mode
  useEffect(() => {
    if (editForm.payment_type !== 'financed') return;
    const total      = Number(editForm.deal_total)              || 0;
    const deposit    = Number(editForm.deposit_amount)           || 0;
    const afterInst  = Number(editForm.remaining_after_install)  || 0;
    const term       = Number(editForm.finance_term_months)      || 0;
    if (term <= 0 || total <= 0) return;
    const toFinance = Math.max(0, total - deposit - afterInst);
    const monthly   = Math.round((toFinance / term) * 100) / 100;
    setEditForm(f => ({ ...f, finance_monthly_amount: monthly }));
  }, [editForm.payment_type, editForm.deal_total, editForm.deposit_amount, editForm.remaining_after_install, editForm.finance_term_months]);

  const startEditSchedule = (schedule) => {
    setScheduleForm({
      scheduled_date: schedule.scheduled_date || '',
      crew_notes: schedule.crew_notes || '',
      payment_terms: schedule.payment_terms || '',
    });
    setEditingSchedule(true);
  };

  const handleCancelSchedule = async () => {
    if (!window.confirm('Cancel this scheduled date? The sale will return to Confirmed — you can reschedule any time.')) return;
    await api.patch(`/schedule/${sale.schedule.id}/status`, { status: 'cancelled' });
    await api.patch(`/sales/${id}/status`, { status: 'confirmed' });
    load();
  };

  const handleSaveSchedule = async () => {
    if (!sale.schedule?.id || !scheduleForm.scheduled_date) return;
    setSavingSchedule(true);
    try {
      await api.put(`/schedule/${sale.schedule.id}`, {
        scheduled_date: scheduleForm.scheduled_date,
        crew_notes: scheduleForm.crew_notes || '',
        payment_terms: scheduleForm.payment_terms || '',
        status: sale.schedule.status || 'scheduled',
      });
      setEditingSchedule(false);
      load();
    } finally {
      setSavingSchedule(false);
    }
  };

  const loadSuggestions = () => {
    setLoadingSuggestions(true);
    api.get(`/schedule/suggest-date?sale_id=${id}`)
      .then(setScheduleSuggestions)
      .catch(() => {})
      .finally(() => setLoadingSuggestions(false));
  };

  useEffect(() => {
    load();
    api.get('/inventory/channels').then(setColors);
  }, [id]);

  if (!sale) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  const activeSteps = sale.color_match_needed
    ? PIPELINE
    : PIPELINE.filter(s => s.key !== 'color_match');
  const currentStep = activeSteps.findIndex(s => s.key === sale.status);
  const canEdit = user.role === 'admin' || user.role === 'manager' ||
    (user.role === 'salesman' && sale.status === 'pending' && sale.salesman_id === user.id);

  const startEdit = () => {
    setEditForm({
      customer_name: sale.customer_name || '',
      address: sale.address || '',
      city: sale.city || '',
      province: sale.province || 'ON',
      phone: sale.phone || '',
      total_footage: sale.total_footage || '',
      power_boxes_350w: sale.power_boxes_350w || 0,
      power_boxes_600w: sale.power_boxes_600w || 0,
      channel_color_id: sale.channel_color_id || '',
      color_name: sale.color_name || '',
      color_match_needed: !!sale.color_match_needed,
      deal_total: sale.deal_total || '',
      payment_type: sale.payment_type || 'cash',
      deposit_amount: sale.deposit_amount || '',
      remaining_after_install: sale.remaining_after_install || '',
      after_install_cash: !!sale.after_install_cash,
      finance_monthly_amount: sale.finance_monthly_amount || '',
      finance_term_months: sale.finance_term_months || '',
      finance_cc_fee_pct: sale.finance_cc_fee_pct || '',
      finance_notes: sale.finance_notes || '',
      ideal_install_date: sale.ideal_install_date || '',
      install_window: sale.install_window || '',
      flexible_schedule: !!sale.flexible_schedule,
      notes: sale.notes || '',
      call_recording: sale.call_recording || '',
      scheduled_date: sale.schedule?.scheduled_date || '',
      crew_notes: sale.schedule?.crew_notes || '',
      payment_terms: sale.schedule?.payment_terms || '',
      salesman_id: sale.salesman_id || '',
    });
    setEditing(true);
  };

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setEditForm(f => ({ ...f, [field]: val }));
  };

  const handleColorChange = (e) => {
    const cid = e.target.value;
    const color = colors.find(c => c.id === Number(cid));
    setEditForm(f => ({ ...f, channel_color_id: cid, color_name: color?.color_name || '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/sales/${id}`, {
        ...(hasManagerAccess(user) && editForm.salesman_id
          ? { salesman_id: Number(editForm.salesman_id) }
          : {}),
        customer_name: editForm.customer_name,
        address: editForm.address,
        city: editForm.city,
        province: editForm.province,
        phone: editForm.phone,
        deal_total: Number(editForm.deal_total) || 0,
        total_footage: Number(editForm.total_footage) || 0,
        power_boxes_350w: Number(editForm.power_boxes_350w) || 0,
        power_boxes_600w: Number(editForm.power_boxes_600w) || 0,
        channel_color_id: Number(editForm.channel_color_id) || null,
        color_name: editForm.color_name || '',
        color_match_needed: editForm.color_match_needed ? 1 : 0,
        payment_type: editForm.payment_type,
        deposit_amount: Number(editForm.deposit_amount) || 0,
        remaining_after_install: Number(editForm.remaining_after_install) || null,
        after_install_cash: editForm.payment_type === 'financed' && editForm.after_install_cash ? 1 : 0,
        finance_monthly_amount: Number(editForm.finance_monthly_amount) || sale.finance_monthly_amount || null,
        finance_term_months: Number(editForm.finance_term_months) || null,
        finance_cc_fee_pct: Number(editForm.finance_cc_fee_pct) || null,
        finance_notes: editForm.finance_notes || null,
        ideal_install_date: editForm.ideal_install_date || null,
        install_window: editForm.install_window || null,
        flexible_schedule: editForm.flexible_schedule ? 1 : 0,
        notes: editForm.notes || '',
        scheduled_date: editForm.scheduled_date || null,
        crew_notes: editForm.crew_notes || null,
        payment_terms: editForm.payment_terms || null,
      });
      setEditing(false);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/sales/${id}/status`, { status: newStatus });
      load();
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/sales/${id}`);
      navigate('/sales');
    } catch (err) { alert(err.message); }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    await api.post('/payments', { sale_id: Number(id), amount: Number(payment.amount), ...payment });
    setShowPaymentForm(false);
    setPayment({ amount: '', payment_method: '', notes: '', payment_date: new Date().toISOString().split('T')[0] });
    load();
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment? This cannot be undone.')) return;
    await api.delete(`/payments/${paymentId}`);
    load();
  };

  const handleRemoveRotessa = async () => {
    if (!window.confirm('Remove the Rotessa/CC setup? This clears the billing start date. Already-recorded payments are kept.')) return;
    await api.delete(`/payments/rotessa-setup/${id}`);
    load();
  };

  const handleSaveRotessa = async (e) => {
    e.preventDefault();
    await api.put(`/payments/rotessa-setup/${id}`, rotessaForm);
    setEditingRotessa(false);
    load();
  };

  // ─── EDIT MODE ───────────────────────────────────────────────────────────────
  if (editing) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setEditing(false)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm">
            <X className="w-4 h-4" /> Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-nova-600 text-white rounded-lg text-sm hover:bg-nova-700 disabled:opacity-50 font-medium">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        <div className="space-y-4">
          {/* Customer */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-4">Customer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Customer Name</label>
                <input className={inputCls} value={editForm.customer_name} onChange={set('customer_name')} />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input className={inputCls} value={editForm.phone} onChange={set('phone')} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Address</label>
                <input className={inputCls} value={editForm.address} onChange={set('address')} />
              </div>
              <div>
                <label className={labelCls}>City</label>
                <input className={inputCls} value={editForm.city} onChange={set('city')} />
              </div>
              <div>
                <label className={labelCls}>Province</label>
                <input className={inputCls} value={editForm.province} onChange={set('province')} />
              </div>
            </div>
          </div>

          {/* Job Details */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-4">Job Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Total Footage</label>
                <input type="number" className={inputCls} value={editForm.total_footage} onChange={set('total_footage')} />
              </div>
              <div>
                <label className={labelCls}>Power Boxes — 350W</label>
                <input type="number" min="0" className={inputCls} value={editForm.power_boxes_350w} onChange={set('power_boxes_350w')} />
              </div>
              <div>
                <label className={labelCls}>Power Boxes — 600W</label>
                <input type="number" min="0" className={inputCls} value={editForm.power_boxes_600w} onChange={set('power_boxes_600w')} />
              </div>
              <div>
                <label className={labelCls}>Channel Color</label>
                <select className={inputCls} value={editForm.channel_color_id} onChange={handleColorChange}>
                  <option value="">Select color…</option>
                  {colors.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.color_name} — {c.effective_available_feet}ft available
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 self-end pb-1">
                <input type="checkbox" id="edit_cm" checked={editForm.color_match_needed} onChange={set('color_match_needed')}
                  className="w-4 h-4 rounded text-nova-600" />
                <label htmlFor="edit_cm" className="text-sm">Needs color match</label>
              </div>
              {hasManagerAccess(user) && (
                <div className="md:col-span-2">
                  <label className={labelCls}>Salesman</label>
                  <select className={inputCls} value={editForm.salesman_id} onChange={set('salesman_id')}>
                    <option value="">— None —</option>
                    {salesmen.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.full_name} ({(s.commission_rate * 100).toFixed(0)}% commission)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-amber-600 mt-1">
                    Changing the salesman will reassign the commission to the new salesman using their rate / tier config.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-4">Payment Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Deal Total</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                  <input type="number" className={`${inputCls} pl-7`} value={editForm.deal_total} onChange={set('deal_total')} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Payment Type</label>
                <select className={inputCls} value={editForm.payment_type} onChange={set('payment_type')}>
                  <option value="cash">Cash</option>
                  <option value="etransfer">E-Transfer</option>
                  <option value="cc">Credit Card</option>
                  <option value="financed">Financed (In-House)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Deposit Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                  <input type="number" className={`${inputCls} pl-7`} value={editForm.deposit_amount} onChange={set('deposit_amount')} />
                </div>
              </div>
              {editForm.payment_type === 'cash' && (
                <div>
                  <label className={labelCls}>Remaining After Install</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                    <input type="number" className={`${inputCls} pl-7`} value={editForm.remaining_after_install} onChange={set('remaining_after_install')} />
                  </div>
                </div>
              )}
              {editForm.payment_type === 'financed' && (
                <>
                  <div>
                    <label className={labelCls}>Amount Paid After Install</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                      <input type="number" className={`${inputCls} pl-7`} value={editForm.remaining_after_install} onChange={set('remaining_after_install')} placeholder="0" />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Lump sum collected at install before monthly billing</p>
                    {Number(editForm.remaining_after_install) > 0 && (
                      <label className="mt-2 flex items-center gap-2 text-xs bg-green-50 border border-green-200 rounded-lg px-3 py-2 cursor-pointer">
                        <input type="checkbox"
                          checked={!!editForm.after_install_cash}
                          onChange={set('after_install_cash')}
                          className="w-3.5 h-3.5 rounded text-green-600 focus:ring-green-500" />
                        <span className="text-green-800 font-medium">After-install paid in CASH (no HST)</span>
                      </label>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Monthly Payment</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                      <input type="number" className={`${inputCls} pl-7`} value={editForm.finance_monthly_amount} onChange={set('finance_monthly_amount')} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Term (months)</label>
                    <input type="number" className={inputCls} value={editForm.finance_term_months} onChange={set('finance_term_months')} />
                  </div>
                  <div>
                    <label className={labelCls}>CC Fee %</label>
                    <input type="number" step="0.1" className={inputCls} value={editForm.finance_cc_fee_pct} onChange={set('finance_cc_fee_pct')} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Finance Notes</label>
                    <input className={inputCls} value={editForm.finance_notes} onChange={set('finance_notes')} />
                  </div>
                </>
              )}
              <p className="text-xs text-gray-500 md:col-span-2">
                {editForm.payment_type === 'cash'
                  ? 'Cash: 13% HST on deposit only. Commission on pre-tax total.'
                  : `Non-cash: pre-tax = $${((Number(editForm.deal_total) || 0) / 1.13).toFixed(2)}`}
              </p>
            </div>
          </div>

          {/* Install Timing */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-4">Install Timing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Ideal Install Date</label>
                <input type="date" className={inputCls} value={editForm.ideal_install_date} onChange={set('ideal_install_date')} />
              </div>
              <div>
                <label className={labelCls}>Install Window</label>
                <input className={inputCls} value={editForm.install_window} onChange={set('install_window')} placeholder="e.g. early April" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="edit_flex" checked={editForm.flexible_schedule} onChange={set('flexible_schedule')}
                  className="w-4 h-4 rounded text-nova-600" />
                <label htmlFor="edit_flex" className="text-sm">Flexible on date</label>
              </div>
            </div>
          </div>

          {/* Schedule (only if already scheduled) */}
          {sale.schedule && (
            <div className="bg-white rounded-xl border p-5">
              <h2 className="font-semibold mb-4">Schedule</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Scheduled Date</label>
                  <input type="date" className={inputCls} value={editForm.scheduled_date} onChange={set('scheduled_date')} />
                </div>
                <div>
                  <label className={labelCls}>Payment Terms (shown to ops)</label>
                  <input className={inputCls} value={editForm.payment_terms} onChange={set('payment_terms')}
                    placeholder="e.g. $500 deposit, $6900 remaining after install" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Crew Notes</label>
                  <input className={inputCls} value={editForm.crew_notes} onChange={set('crew_notes')} />
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-4">Notes</h2>
            <textarea className={`${inputCls} h-24`} value={editForm.notes} onChange={set('notes')}
              placeholder="Contact instructions, special requests, color match notes…" />
          </div>

          {/* Call Recording */}
          <div className="bg-white rounded-xl border p-5">
            <h2 className="font-semibold mb-1">Call Recording</h2>
            <p className="text-xs text-gray-400 mb-3">Link to call recording for this intake</p>
            <input className={inputCls} value={editForm.call_recording || ''} onChange={set('call_recording')}
              placeholder="https://..." type="url" />
          </div>
        </div>

        <div className="flex gap-3 mt-4 pb-8">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-nova-600 text-white rounded-xl text-sm hover:bg-nova-700 disabled:opacity-50 font-medium text-base">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
          <button onClick={() => setEditing(false)}
            className="px-8 py-3 bg-white border text-gray-600 rounded-xl text-sm hover:bg-gray-50 font-medium">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ─── VIEW MODE ───────────────────────────────────────────────────────────────
  const remaining = (sale.deal_total || 0) - (sale.total_paid || 0);

  return (
    <div className="max-w-4xl mx-auto">
      <button onClick={() => navigate('/sales')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to Pipeline
      </button>

      {/* ── PIPELINE STEPPER ─────────────────────────────────────────────── */}
      {sale.status !== 'cancelled' && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          {/* Customer header inside stepper card */}
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div>
              <h1 className="text-xl font-bold">{sale.customer_name}</h1>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                <MapPin className="w-3.5 h-3.5" />
                {sale.address}{sale.city ? `, ${sale.city}` : ''}{sale.province ? `, ${sale.province}` : ''}
              </p>
              {sale.phone && <p className="text-xs text-gray-400 mt-0.5">{sale.phone}</p>}
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-2xl font-bold">{fmt(sale.deal_total)}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${payColor[sale.payment_type] || 'bg-gray-100 text-gray-600'}`}>
                  {paymentLabels[sale.payment_type] || sale.payment_type}
                </span>
              </div>
              {canEdit && (
                <button onClick={startEdit}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium">
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
              )}
            </div>
          </div>

          {/* Step indicators */}
          <div className="flex items-center">
            {activeSteps.map((step, i) => {
              const isDone = i < currentStep;
              const isCurrent = i === currentStep;
              const isLast = i === activeSteps.length - 1;
              return (
                <div key={step.key} className="flex items-center flex-1 min-w-0">
                  {/* Step circle + label */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                      isDone   ? 'bg-green-500 border-green-500 text-white' :
                      isCurrent ? 'bg-nova-600 border-nova-600 text-white shadow-md ring-4 ring-nova-100' :
                                  'bg-white border-gray-200 text-gray-400'
                    }`}>
                      {isDone ? <CheckCircle2 className="w-5 h-5" /> : <span className="text-xs">{i + 1}</span>}
                    </div>
                    <span className={`text-[10px] mt-1 text-center leading-tight font-medium ${
                      isCurrent ? 'text-nova-700' : isDone ? 'text-green-600' : 'text-gray-400'
                    }`} style={{ maxWidth: 56 }}>
                      {step.label}
                    </span>
                  </div>
                  {/* Connector */}
                  {!isLast && (
                    <div className={`flex-1 h-0.5 mx-1 mb-4 ${i < currentStep ? 'bg-green-400' : 'bg-gray-200'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Current stage description + action */}
          {currentStep >= 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 pt-4 border-t">
              <div>
                <p className="text-sm font-semibold text-gray-700">
                  {activeSteps[currentStep]?.icon} {activeSteps[currentStep]?.label}
                </p>
                <p className="text-xs text-gray-500">{activeSteps[currentStep]?.desc}</p>
              </div>
              {user.role === 'admin' || user.role === 'manager' && (
                <div className="flex flex-wrap gap-2">
                  {sale.status === 'pending' && (
                    <button onClick={() => handleStatusChange(sale.color_match_needed ? 'color_match' : 'confirmed')}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 font-medium">
                      Confirm Sale →
                    </button>
                  )}
                  {sale.status === 'color_match' && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5">
                        <Palette className="w-4 h-4 text-purple-500 shrink-0" />
                        <select
                          value={matchedColorId}
                          onChange={e => setMatchedColorId(e.target.value)}
                          className="text-sm bg-transparent outline-none text-gray-700 pr-1"
                        >
                          <option value="">Select matched color…</option>
                          {colors.map(c => (
                            <option key={c.id} value={c.id}>{c.color_name}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        disabled={!matchedColorId}
                        onClick={async () => {
                          const color = colors.find(c => c.id === Number(matchedColorId));
                          await api.put(`/sales/${id}`, {
                            channel_color_id: Number(matchedColorId),
                            color_name: color?.color_name || '',
                          });
                          handleStatusChange('confirmed');
                          setMatchedColorId('');
                        }}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                        Color Received →
                      </button>
                    </div>
                  )}
                  {sale.status === 'confirmed' && (
                    <button onClick={() => navigate(`/schedule?sale_id=${id}`)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 font-medium flex items-center gap-1.5">
                      <Calendar className="w-4 h-4" /> Schedule Install →
                    </button>
                  )}
                  {sale.status === 'scheduled' && (
                    <button onClick={() => handleStatusChange('in_progress')}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 font-medium">
                      Start Install →
                    </button>
                  )}
                  {sale.status === 'in_progress' && (() => {
                    const remaining = (sale.deal_total || 0) - (sale.total_paid || 0);
                    const canCollect = Number(collectPayment.amount) > 0 && collectPayment.method;
                    return (
                      <div className="flex flex-col gap-2 w-full">
                        {/* Inline payment entry — required to go straight to Collected */}
                        <div className="flex flex-wrap items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3">
                          <DollarSign className="w-4 h-4 text-green-600 shrink-0" />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={`Amount (owing: $${remaining.toFixed(2)})`}
                            value={collectPayment.amount}
                            onChange={e => setCollectPayment(p => ({ ...p, amount: e.target.value }))}
                            className="w-40 px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-400 bg-white"
                          />
                          <select
                            value={collectPayment.method}
                            onChange={e => setCollectPayment(p => ({ ...p, method: e.target.value }))}
                            className="px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-400 bg-white"
                          >
                            <option value="">Payment method…</option>
                            <option value="cash">Cash</option>
                            <option value="etransfer">E-Transfer</option>
                            <option value="cc">Credit Card</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Notes (optional)"
                            value={collectPayment.notes}
                            onChange={e => setCollectPayment(p => ({ ...p, notes: e.target.value }))}
                            className="flex-1 min-w-32 px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-400 bg-white"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            disabled={!canCollect}
                            onClick={async () => {
                              await api.post('/payments', {
                                sale_id: Number(id),
                                amount: Number(collectPayment.amount),
                                payment_method: collectPayment.method,
                                notes: collectPayment.notes || 'Payment collected on install',
                                payment_date: new Date().toISOString().split('T')[0],
                              });
                              setCollectPayment({ amount: '', method: '', notes: '' });
                              handleStatusChange('completed');
                            }}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4" /> Installed — Paid ✓
                          </button>
                          <button onClick={() => handleStatusChange('awaiting_payment')}
                            className="px-4 py-2 bg-rose-500 text-white rounded-lg text-sm hover:bg-rose-600 font-medium">
                            Installed — Awaiting Payment
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {sale.status === 'awaiting_payment' && (() => {
                    const remaining = (sale.deal_total || 0) - (sale.total_paid || 0);
                    const canCollect = Number(collectPayment.amount) > 0 && collectPayment.method;
                    return (
                      <div className="flex flex-col gap-2 w-full">
                        <div className="flex flex-wrap items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3">
                          <DollarSign className="w-4 h-4 text-green-600 shrink-0" />
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder={`Amount (owing: $${remaining.toFixed(2)})`}
                            value={collectPayment.amount}
                            onChange={e => setCollectPayment(p => ({ ...p, amount: e.target.value }))}
                            className="w-40 px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-400 bg-white"
                          />
                          <select
                            value={collectPayment.method}
                            onChange={e => setCollectPayment(p => ({ ...p, method: e.target.value }))}
                            className="px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-400 bg-white"
                          >
                            <option value="">Payment method…</option>
                            <option value="cash">Cash</option>
                            <option value="etransfer">E-Transfer</option>
                            <option value="cc">Credit Card</option>
                          </select>
                          <input
                            type="text"
                            placeholder="Notes (optional)"
                            value={collectPayment.notes}
                            onChange={e => setCollectPayment(p => ({ ...p, notes: e.target.value }))}
                            className="flex-1 min-w-32 px-2 py-1.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-400 bg-white"
                          />
                        </div>
                        <button
                          disabled={!canCollect}
                          onClick={async () => {
                            await api.post('/payments', {
                              sale_id: Number(id),
                              amount: Number(collectPayment.amount),
                              payment_method: collectPayment.method,
                              notes: collectPayment.notes || 'Final payment on install',
                              payment_date: new Date().toISOString().split('T')[0],
                            });
                            setCollectPayment({ amount: '', method: '', notes: '' });
                            handleStatusChange('completed');
                          }}
                          className="self-start px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4" /> Record Payment & Mark Collected ✓
                        </button>
                      </div>
                    );
                  })()}
                  <button onClick={() => handleStatusChange('cancelled')}
                    className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm hover:bg-red-100">
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Cancelled banner */}
      {sale.status === 'cancelled' && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold">{sale.customer_name}</h1>
              <p className="text-sm text-gray-500 mt-0.5">{sale.address}{sale.city ? `, ${sale.city}` : ''}</p>
            </div>
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">Cancelled</span>
          </div>
          {user.role === 'admin' || user.role === 'manager' && (
            <button onClick={() => handleStatusChange('pending')}
              className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700">
              Reactivate Sale
            </button>
          )}
        </div>
      )}

      {/* ── SCHEDULING PANEL (admin only, confirmed but not yet scheduled) ── */}
      {user.role === 'admin' || user.role === 'manager' && ['pending', 'confirmed'].includes(sale.status) && !sale.schedule && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-nova-600" /> Install Date Planning
            </h2>
          </div>

          {/* Salesman's request chips */}
          <div className="flex flex-wrap gap-2 mb-4">
            {sale.ideal_install_date ? (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                <Clock className="w-3.5 h-3.5" />
                Requested: <strong className="ml-1">{new Date(...sale.ideal_install_date.split('-').map(Number)).toLocaleDateString('en-CA', { weekday:'short', month:'short', day:'numeric' })}</strong>
              </div>
            ) : sale.install_window ? (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-sm">
                <Clock className="w-3.5 h-3.5" /> Window: <strong className="ml-1">{sale.install_window}</strong>
              </div>
            ) : (
              <div className="text-sm text-gray-400 italic">No install date specified</div>
            )}
            {sale.flexible_schedule ? (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm">
                <CheckCircle2 className="w-3.5 h-3.5" /> Flexible
              </span>
            ) : sale.ideal_install_date ? (
              <span className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-sm">Prefers this date</span>
            ) : null}
            {sale.city && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg text-sm">
                <MapPin className="w-3.5 h-3.5" /> {sale.city}
              </span>
            )}
          </div>

          {sale.status === 'pending' && (
            <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              Confirm this sale first before scheduling an install date.
            </p>
          )}

          {sale.status === 'confirmed' && (
            <>
              {loadingSuggestions && <p className="text-sm text-gray-400 animate-pulse">Finding optimal dates…</p>}
              {scheduleSuggestions && (
                <>
                  <p className="text-xs text-gray-500 mb-2">Click a date to open the schedule form:</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {scheduleSuggestions.suggestions.map(s => {
                      const [y,m,d] = s.date.split('-').map(Number);
                      const label = new Date(y,m-1,d).toLocaleDateString('en-CA', { weekday:'short', month:'short', day:'numeric' });
                      const isIdeal = s.type === 'ideal' || s.reasons.includes("Salesman's ideal date");
                      return (
                        <button key={s.date}
                          onClick={() => navigate(`/schedule?sale_id=${id}&date=${s.date}`)}
                          className={`text-left p-3 rounded-xl border-2 hover:shadow-md transition-shadow ${
                            isIdeal ? 'border-nova-400 bg-nova-50' :
                            s.same_city ? 'border-green-300 bg-green-50' :
                            'border-gray-200 bg-white hover:border-gray-300'
                          }`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-sm">{label}</span>
                            {isIdeal && <span className="text-[10px] bg-nova-100 text-nova-700 px-1.5 py-0.5 rounded-full font-medium">Ideal</span>}
                            {!isIdeal && s.same_city && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-medium">Route match</span>}
                          </div>
                          {s.cities.length > 0 && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mb-1">
                              <MapPin className="w-3 h-3" /> {s.cities.join(', ')}
                            </p>
                          )}
                          <div className="text-xs text-gray-400">
                            {s.existing_footage > 0 ? `${s.existing_footage}ft + ${sale.total_footage}ft = ${s.new_total}ft` : `Open day — ${s.new_total}ft`}
                          </div>
                          {s.reasons[0] && <p className="text-[11px] text-nova-600 mt-1 font-medium">{s.reasons[0]}</p>}
                        </button>
                      );
                    })}
                  </div>
                  {scheduleSuggestions.suggestions.length === 0 && (
                    <p className="text-sm text-gray-400">No optimal dates found. <button onClick={() => navigate(`/schedule?sale_id=${id}`)} className="text-nova-600 hover:underline">Schedule manually →</button></p>
                  )}
                  <button onClick={() => navigate(`/schedule?sale_id=${id}`)} className="mt-3 text-xs text-gray-400 hover:text-nova-600 hover:underline">
                    Pick a different date manually →
                  </button>
                </>
              )}
              {!scheduleSuggestions && !loadingSuggestions && (
                <button onClick={loadSuggestions}
                  className="w-full py-2.5 border-2 border-dashed border-nova-200 text-nova-600 rounded-xl text-sm hover:bg-nova-50 font-medium flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4" /> Get Route-Optimized Date Suggestions
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── CONTENT GRID ─────────────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-3 gap-4">

        {/* Job Details — 2 cols */}
        <div className="bg-white rounded-xl border p-5 lg:col-span-2">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <Ruler className="w-4 h-4 text-gray-400" /> Job Details
          </h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <div className="flex justify-between col-span-2 sm:col-span-1">
              <dt className="text-gray-500">Footage</dt><dd className="font-medium">{sale.total_footage}ft</dd>
            </div>
            <div className="flex justify-between col-span-2 sm:col-span-1">
              <dt className="text-gray-500">Color</dt>
              <dd className="flex items-center gap-1">
                <Palette className="w-3 h-3 text-gray-400" />
                {sale.color_name || '—'}
                {sale.color_match_needed && <StatusBadge status={sale.color_match_status} />}
              </dd>
            </div>
            {(sale.power_boxes_350w > 0 || sale.power_boxes_600w > 0) && (
              <div className="flex justify-between col-span-2">
                <dt className="text-gray-500">Power Boxes</dt>
                <dd>
                  {sale.power_boxes_350w > 0 && `${sale.power_boxes_350w}× 350W`}
                  {sale.power_boxes_350w > 0 && sale.power_boxes_600w > 0 && ' · '}
                  {sale.power_boxes_600w > 0 && `${sale.power_boxes_600w}× 600W`}
                </dd>
              </div>
            )}
            <div className="flex justify-between col-span-2 sm:col-span-1">
              <dt className="text-gray-500">Salesman</dt>
              <dd className="flex items-center gap-1"><User className="w-3 h-3 text-gray-400" />{sale.salesman_name}</dd>
            </div>
            <div className="flex justify-between col-span-2 sm:col-span-1">
              <dt className="text-gray-500">Commission</dt>
              <dd>{fmt(sale.commission_amount)} <span className="text-gray-400">({(sale.commission_rate * 100).toFixed(0)}%)</span></dd>
            </div>
            {sale.manager_amount > 0 && user?.role === 'admin' && (
              <div className="flex justify-between col-span-2">
                <dt className="text-gray-500">Manager Cut</dt>
                <dd>{fmt(sale.manager_amount)} <span className="text-gray-400">({(sale.manager_rate * 100).toFixed(0)}%)</span></dd>
              </div>
            )}
            {sale.ideal_install_date && (
              <div className="flex justify-between col-span-2">
                <dt className="text-gray-500">Requested Date</dt>
                <dd>{new Date(sale.ideal_install_date + 'T00:00:00').toLocaleDateString('en-CA', { weekday:'short', month:'short', day:'numeric', year:'numeric' })}
                  {sale.flexible_schedule && <span className="ml-1 text-green-600 text-xs">· flexible</span>}
                </dd>
              </div>
            )}
            {sale.install_window && !sale.ideal_install_date && (
              <div className="flex justify-between col-span-2">
                <dt className="text-gray-500">Install Window</dt><dd>{sale.install_window}</dd>
              </div>
            )}
          </dl>
          {sale.notes && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm text-gray-600 whitespace-pre-line">{sale.notes}</div>
          )}
          {sale.call_recording && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2">
              <span className="text-xs font-medium text-blue-700">📞 Call Recording:</span>
              <a href={sale.call_recording} target="_blank" rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:underline truncate">{sale.call_recording}</a>
            </div>
          )}
        </div>

        {/* Financial Summary — 1 col */}
        <div className="bg-white rounded-xl border p-5 flex flex-col">
          <h2 className="font-semibold mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-400" /> Financials
          </h2>
          <dl className="space-y-2 text-sm flex-1">
            <div className="flex justify-between">
              <dt className="text-gray-500">Pre-Tax</dt><dd>{fmt(sale.pre_tax_amount)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">HST (13%)</dt><dd>{fmt(sale.tax_amount)}</dd>
            </div>
            <div className="flex justify-between font-semibold border-t pt-2">
              <dt>Total</dt><dd>{fmt(sale.deal_total)}</dd>
            </div>
            {sale.deposit_amount > 0 && (
              <div className="flex justify-between text-green-700">
                <dt>Deposit</dt><dd>{fmt(sale.deposit_amount)}</dd>
              </div>
            )}
            <div className="flex justify-between text-green-600">
              <dt>Collected</dt><dd>{fmt(sale.total_paid)}</dd>
            </div>
            {(() => {
              const rotessaActive = sale.payment_type === 'financed' && !!sale.finance_start_date;
              const rotessaNeeded = sale.payment_type === 'financed' && !sale.finance_start_date && remaining > 0.5;
              if (rotessaActive && remaining > 0.5) {
                return (
                  <div className="flex justify-between font-bold text-base text-purple-600">
                    <dt>Auto-collecting</dt><dd>{fmt(remaining)}</dd>
                  </div>
                );
              }
              return (
                <div className={`flex justify-between font-bold text-base ${remaining > 0.5 ? 'text-red-500' : 'text-green-600'}`}>
                  <dt>{remaining > 0.5 ? 'Owing' : 'Paid ✓'}</dt>
                  <dd>{remaining > 0.5 ? fmt(remaining) : fmt(sale.deal_total)}</dd>
                </div>
              );
            })()}
            {sale.payment_type === 'financed' && sale.finance_start_date && remaining > 0.5 && (
              <div className="p-2 bg-purple-50 border border-purple-200 rounded-lg text-xs text-purple-700 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                Rotessa / CC billing active · {fmt(sale.finance_monthly_amount)}/mo × {sale.finance_term_months} mo
                {sale.finance_cc_fee_pct > 0 && ` · ${sale.finance_cc_fee_pct}% CC fee`}
              </div>
            )}
            {sale.payment_type === 'financed' && !sale.finance_start_date && remaining > 0.5 && (
              <div className="p-2 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-700">
                ⚠ Rotessa / CC not set up yet — {fmt(sale.finance_monthly_amount)}/mo × {sale.finance_term_months} mo
              </div>
            )}
            {sale.payment_type !== 'financed' && sale.finance_monthly_amount > 0 && (
              <div className="p-2 bg-orange-50 rounded-lg text-xs text-orange-700">
                {fmt(sale.finance_monthly_amount)}/mo × {sale.finance_term_months} months
                {sale.finance_cc_fee_pct > 0 && ` · ${sale.finance_cc_fee_pct}% CC fee`}
              </div>
            )}
            {sale.remaining_after_install > 0 && (
              <div className="p-2 bg-amber-50 rounded-lg text-xs text-amber-700">
                {fmt(sale.remaining_after_install)} due after install
              </div>
            )}
          </dl>
          {(() => {
            const payTab = (sale.payment_type === 'financed' && sale.finance_start_date)
              ? 'recurring'
              : 'collections';
            return (
              <Link
                to={`/payments?sale_id=${sale.id}&tab=${payTab}`}
                className="mt-4 text-xs text-nova-600 hover:underline flex items-center gap-1 justify-end"
              >
                View in Payments <ChevronRight className="w-3 h-3" />
              </Link>
            );
          })()}
        </div>

        {/* Payment History */}
        <div className="bg-white rounded-xl border p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Payment History</h2>
            {user.role === 'admin' || user.role === 'manager' && (
              <button onClick={() => setShowPaymentForm(!showPaymentForm)}
                className="text-sm text-nova-600 hover:underline flex items-center gap-1">
                <Plus className="w-4 h-4" /> Record
              </button>
            )}
          </div>
          {showPaymentForm && (
            <form onSubmit={handlePayment} className="bg-gray-50 rounded-lg p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <input type="number" step="0.01" placeholder="Amount" required className="px-3 py-2 border rounded-lg text-sm"
                value={payment.amount} onChange={e => setPayment(p => ({ ...p, amount: e.target.value }))} />
              <select className="px-3 py-2 border rounded-lg text-sm"
                value={payment.payment_method} onChange={e => setPayment(p => ({ ...p, payment_method: e.target.value }))}>
                <option value="">Method</option>
                <option value="cash">Cash</option>
                <option value="etransfer">E-Transfer</option>
                <option value="cc">CC</option>
              </select>
              <input type="date" className="px-3 py-2 border rounded-lg text-sm"
                value={payment.payment_date} onChange={e => setPayment(p => ({ ...p, payment_date: e.target.value }))} />
              <button type="submit" className="bg-nova-600 text-white rounded-lg text-sm hover:bg-nova-700">Save</button>
            </form>
          )}
          <div className="divide-y">
            {/* Rotessa setup row — shown separately with edit/remove */}
            {sale.payments?.filter(p => p.payment_method === 'rotessa_setup').map(p => (
              <div key={p.id} className="py-2">
                {editingRotessa ? (
                  <form onSubmit={handleSaveRotessa} className="bg-purple-50 rounded-lg p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Billing Start Date</label>
                        <input type="date" required className="w-full px-2 py-1.5 border rounded text-sm"
                          value={rotessaForm.start_date} onChange={e => setRotessaForm(f => ({ ...f, start_date: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 block mb-1">Rotessa Link</label>
                        <input type="url" className="w-full px-2 py-1.5 border rounded text-sm" placeholder="https://..."
                          value={rotessaForm.rotessa_link} onChange={e => setRotessaForm(f => ({ ...f, rotessa_link: e.target.value }))} />
                      </div>
                    </div>
                    <input className="w-full px-2 py-1.5 border rounded text-sm" placeholder="Notes"
                      value={rotessaForm.notes} onChange={e => setRotessaForm(f => ({ ...f, notes: e.target.value }))} />
                    <div className="flex gap-2">
                      <button type="submit" className="bg-purple-600 text-white px-3 py-1.5 rounded text-xs hover:bg-purple-700">Save</button>
                      <button type="button" onClick={() => setEditingRotessa(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">Rotessa/CC Setup</span>
                      <span className="text-gray-500 text-xs">Billing starts {p.payment_date}</span>
                      {p.rotessa_link && <a href={p.rotessa_link} target="_blank" rel="noreferrer" className="text-xs text-nova-600 hover:underline">View</a>}
                    </div>
                    {(user.role === 'admin') && (
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setRotessaForm({ start_date: p.payment_date, rotessa_link: p.rotessa_link || '', notes: p.notes || '' }); setEditingRotessa(true); }}
                          className="text-xs text-gray-400 hover:text-nova-600 flex items-center gap-1">
                          <Edit2 className="w-3 h-3" /> Edit
                        </button>
                        <button onClick={handleRemoveRotessa}
                          className="text-gray-300 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {/* Regular payments */}
            {sale.payments?.filter(p => p.payment_method !== 'rotessa_setup').map(p => (
              <div key={p.id} className="py-2 flex justify-between items-center text-sm">
                <div>
                  <span className="font-medium">{fmt(p.amount)}</span>
                  {p.payment_method && <span className="text-gray-400 ml-2 capitalize">{p.payment_method.replace(/_/g, ' ')}</span>}
                  {p.notes && <span className="text-gray-400 ml-2">— {p.notes}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">{p.payment_date}</span>
                  {hasManagerAccess(user) && (
                    <>
                      <button onClick={() => openEditPayment(p)}
                        className="text-gray-300 hover:text-nova-600 hover:bg-nova-50 p-1 rounded transition-colors"
                        title="Edit payment">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDeletePayment(p.id)}
                        className="text-gray-300 hover:text-red-500 hover:bg-red-50 p-1 rounded transition-colors"
                        title="Delete payment">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {!sale.payments?.filter(p => p.payment_method !== 'rotessa_setup').length && (
              <p className="text-gray-400 text-sm py-2">No payments recorded yet</p>
            )}
          </div>
        </div>

        {/* Schedule card */}
        {sale.schedule && (
          <div className="bg-white rounded-xl border p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-400" />
                {sale.status === 'completed' ? 'Installed & Collected' : sale.status === 'awaiting_payment' ? 'Awaiting Payment' : sale.status === 'in_progress' ? 'Day of Install' : 'Scheduled Install'}
              </h2>
              {(user.role === 'admin' || user.role === 'manager') && !editingSchedule && (
                <div className="flex items-center gap-3">
                  <button onClick={() => startEditSchedule(sale.schedule)}
                    className="text-xs text-nova-600 hover:underline flex items-center gap-1">
                    <Edit2 className="w-3 h-3" /> Reschedule
                  </button>
                  {['scheduled', 'confirmed'].includes(sale.status) && (
                    <button onClick={handleCancelSchedule}
                      className="text-xs text-red-400 hover:text-red-600 hover:underline flex items-center gap-1">
                      <X className="w-3 h-3" /> Cancel Schedule
                    </button>
                  )}
                </div>
              )}
            </div>

            {editingSchedule ? (
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>New Date</label>
                  <input type="date" className={inputCls}
                    value={scheduleForm.scheduled_date}
                    onChange={e => setScheduleForm(f => ({ ...f, scheduled_date: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Payment Terms</label>
                  <input className={inputCls} placeholder="e.g. $500 deposit, $2000 on install"
                    value={scheduleForm.payment_terms}
                    onChange={e => setScheduleForm(f => ({ ...f, payment_terms: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Crew Notes</label>
                  <input className={inputCls} placeholder="Gate code, access notes…"
                    value={scheduleForm.crew_notes}
                    onChange={e => setScheduleForm(f => ({ ...f, crew_notes: e.target.value }))} />
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={handleSaveSchedule} disabled={savingSchedule || !scheduleForm.scheduled_date}
                    className="flex items-center gap-1.5 px-3 py-2 bg-nova-600 text-white rounded-lg text-sm hover:bg-nova-700 disabled:opacity-50">
                    <Save className="w-3.5 h-3.5" />{savingSchedule ? 'Saving…' : 'Save'}
                  </button>
                  <button onClick={() => setEditingSchedule(false)}
                    className="px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Date</dt>
                    <dd className="font-semibold text-indigo-700">
                      {new Date(sale.schedule.scheduled_date + 'T00:00:00').toLocaleDateString('en-CA', { weekday:'short', month:'short', day:'numeric', year:'numeric' })}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Install Status</dt>
                    <dd>
                      {sale.status === 'completed'
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" /> Installed & Collected</span>
                        : sale.status === 'awaiting_payment'
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-100 text-rose-700">💳 Awaiting Payment</span>
                        : sale.status === 'in_progress'
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">In Progress</span>
                        : <StatusBadge status={sale.schedule.status} />}
                    </dd>
                  </div>
                  {sale.schedule.payment_terms && (
                    <div>
                      <dt className="text-gray-500 mb-1">Payment Terms</dt>
                      <dd className="p-2 bg-amber-50 text-amber-700 rounded text-xs">{sale.schedule.payment_terms}</dd>
                    </div>
                  )}
                  {sale.schedule.crew_notes && (
                    <div><dt className="text-gray-500">Crew Notes</dt><dd className="mt-1">{sale.schedule.crew_notes}</dd></div>
                  )}
                </dl>
                <Link
                  to={`/schedule?date=${sale.schedule.scheduled_date}&highlight=${sale.id}`}
                  className="mt-3 text-xs text-nova-600 hover:underline flex items-center gap-1 justify-end"
                >
                  View in Schedule <ChevronRight className="w-3 h-3" />
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      {/* Delete zone */}
      {user.role === 'admin' || user.role === 'manager' && (
        <div className="mt-6 border-t pt-4 flex justify-end">
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)}
              className="text-xs text-red-400 hover:text-red-600 hover:underline">
              Delete this sale
            </button>
          ) : (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="text-sm text-red-700 font-medium">Permanently delete?</span>
              <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700">Yes, Delete</button>
              <button onClick={() => setConfirmDelete(false)} className="px-3 py-1 bg-white border text-gray-600 rounded text-xs hover:bg-gray-50">Cancel</button>
            </div>
          )}
        </div>
      )}

      {/* Edit payment modal */}
      {editingPayment && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeEditPayment}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Edit Payment</h3>
              <button onClick={closeEditPayment}><X className="w-4 h-4 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 text-sm">$</span>
                  <input type="number" step="0.01" className={`${inputCls} pl-7`}
                    value={editPaymentForm.amount}
                    onChange={e => setEditPaymentForm(f => ({ ...f, amount: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Method</label>
                <select className={inputCls}
                  value={editPaymentForm.payment_method}
                  onChange={e => setEditPaymentForm(f => ({ ...f, payment_method: e.target.value }))}>
                  <option value="">— None —</option>
                  <option value="cash">Cash</option>
                  <option value="etransfer">E-Transfer</option>
                  <option value="cc">Credit Card</option>
                  <option value="financed">Financed</option>
                  <option value="rotessa_auto">Rotessa Auto</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Payment Date</label>
                <input type="date" className={inputCls}
                  value={editPaymentForm.payment_date}
                  onChange={e => setEditPaymentForm(f => ({ ...f, payment_date: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Notes</label>
                <input className={inputCls}
                  value={editPaymentForm.notes}
                  onChange={e => setEditPaymentForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={saveEditPayment} disabled={savingPaymentEdit}
                className="flex-1 py-2 bg-nova-600 hover:bg-nova-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-1">
                <Save className="w-3.5 h-3.5" />{savingPaymentEdit ? 'Saving…' : 'Save'}
              </button>
              <button onClick={closeEditPayment}
                className="px-4 py-2 bg-white border text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
